import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';

const CACHE_KEY = 'tahminarena_daily_ai_cards_stable_v5';

const EMPTY_SUMMARY = {
  total: 0,
  systemPicks: 0,
  strongTrends: 0,
  liveWatch: 0,
  risky: 0,
  dataSignals: 0
};

const EMPTY_STATS = {
  totalCards: 0,
  premiumCards: 0,
  valueBets: 0,
  pending: 0,
  won: 0,
  lost: 0,
  void: 0
};

const getClientIstanbulDateKey = () => {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Istanbul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
};

const buildQuery = (dateKey = '') => {
  const params = new URLSearchParams();

  if (dateKey) {
    params.set('date', dateKey);
    params.set('dateKey', dateKey);
  }

  params.set('limit', '200');
  params.set('days', '3');
  params.set('includeWindow', 'true');
  params.set('_', Date.now().toString());

  return params.toString();
};

const unwrapPayload = (payload) => {
  if (!payload) return {};
  if (Array.isArray(payload)) return { cards: payload };

  if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
    return payload.data;
  }

  if (payload.payload && typeof payload.payload === 'object' && !Array.isArray(payload.payload)) {
    return payload.payload;
  }

  if (payload.result && typeof payload.result === 'object' && !Array.isArray(payload.result)) {
    return payload.result;
  }

  return payload;
};

const extractCards = (payload) => {
  const normalized = unwrapPayload(payload);

  if (Array.isArray(normalized)) return normalized;

  const keys = [
    'cards',
    'dailyAiCards',
    'analyses',
    'items',
    'publishedCards',
    'results',
    'matches',
    'data'
  ];

  for (const key of keys) {
    if (Array.isArray(normalized?.[key]) && normalized[key].length > 0) {
      return normalized[key];
    }
  }

  const nested = [
    normalized?.data,
    normalized?.payload,
    normalized?.result,
    normalized?.publication,
    normalized?.published
  ];

  for (const item of nested) {
    if (!item || item === normalized) continue;
    const cards = extractCards(item);
    if (cards.length > 0) return cards;
  }

  return [];
};

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.cards) ? parsed.cards : [];
  } catch {
    return [];
  }
};

const writeCache = (cards) => {
  try {
    if (!Array.isArray(cards) || cards.length === 0) return;

    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        savedAt: Date.now(),
        cards
      })
    );
  } catch {
    // cache yazilamazsa akisi bozma
  }
};

const normalizeErrorMessage = (error) => {
  const status = Number(error?.status || error?.responseStatus || 0);
  if (status === 401) return '401: AI analizleri icin oturum gerekli.';
  if (status === 403) return '403: AI analizleri icin yetki gerekli.';
  return error?.message || 'Analizler su anda yuklenemedi.';
};

const getPayloadKeys = (payload) => {
  const normalized = unwrapPayload(payload);
  return normalized && typeof normalized === 'object' && !Array.isArray(normalized)
    ? Object.keys(normalized)
    : [];
};

const buildStats = (payload, cards) => {
  const normalized = unwrapPayload(payload);
  const backendStats = normalized?.stats && typeof normalized.stats === 'object'
    ? normalized.stats
    : {};

  const pending = cards.filter((card) => String(card?.predictionStatus || 'pending').trim().toLowerCase() === 'pending').length;
  const won = cards.filter((card) => String(card?.predictionStatus || '').trim().toLowerCase() === 'won').length;
  const lost = cards.filter((card) => String(card?.predictionStatus || '').trim().toLowerCase() === 'lost').length;
  const voidCount = cards.filter((card) => ['void', 'cancelled', 'canceled'].includes(String(card?.predictionStatus || '').trim().toLowerCase())).length;

  return {
    ...EMPTY_STATS,
    ...backendStats,
    totalCards: Math.max(Number(backendStats.totalCards || backendStats.cardCount || 0), cards.length),
    premiumCards: Math.max(Number(backendStats.premiumCards || 0), cards.filter((card) => Boolean(card?.premiumEligible)).length),
    valueBets: Math.max(Number(backendStats.valueBets || 0), cards.filter((card) => Boolean(card?.valueBet) || String(card?.category || '').toUpperCase() === 'VALUE_BET').length),
    pending: Math.max(Number(backendStats.pending || 0), pending),
    won: Math.max(Number(backendStats.won || 0), won),
    lost: Math.max(Number(backendStats.lost || 0), lost),
    void: Math.max(Number(backendStats.void || 0), voidCount)
  };
};

const buildSummary = (payload, cards) => {
  const normalized = unwrapPayload(payload);
  const backendSummary = normalized?.summary && typeof normalized.summary === 'object'
    ? normalized.summary
    : {};

  return {
    ...EMPTY_SUMMARY,
    ...backendSummary,
    total: Math.max(Number(backendSummary.total || 0), cards.length)
  };
};

export const useDailyAiCards = (selectedDateKey = '') => {
  const timeoutRef = useRef(null);
  const [cards, setCards] = useState(() => readCache());
  const [status, setStatus] = useState('empty');
  const [summary, setSummary] = useState({ ...EMPTY_SUMMARY });
  const [stats, setStats] = useState({ ...EMPTY_STATS });
  const [todayKey, setTodayKey] = useState(null);
  const [publicationDate, setPublicationDate] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [source, setSource] = useState('empty');
  const [refreshSlot, setRefreshSlot] = useState('');
  const [message, setMessage] = useState('');
  const [debug, setDebug] = useState(null);
  const [history, setHistory] = useState([]);
  const [adminNotice, setAdminNotice] = useState('');
  const [loading, setLoading] = useState(cards.length === 0);
  const [error, setError] = useState('');
  const [endpoint, setEndpoint] = useState('/api/daily-ai-cards');
  const [fetchDebug, setFetchDebug] = useState({
    endpoint: '/api/daily-ai-cards',
    payloadKeys: [],
    extractedCards: cards.length,
    firstCardSample: cards[0] || null
  });

  const clearPendingPoll = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const fetchCards = useCallback(async () => {
    const requestDateKey = String(selectedDateKey || getClientIstanbulDateKey()).trim();
    const primaryEndpoint = `/daily-ai-cards?${buildQuery(requestDateKey)}`;
    const fallbackEndpoint = `/ai-analysis-published?${buildQuery(requestDateKey)}`;
    const latestEndpoint = `/daily-ai-cards?${buildQuery('')}`;
    const endpoints = [primaryEndpoint, fallbackEndpoint, latestEndpoint];

    setLoading(true);

    let lastError = null;
    let selectedPayload = null;
    let selectedCards = [];
    let selectedEndpoint = primaryEndpoint;

    for (const currentEndpoint of endpoints) {
      try {
        const payload = await api.request(currentEndpoint);
        const normalized = unwrapPayload(payload);
        const extractedCards = extractCards(normalized).slice(0, 200);

        console.log('AI_ANALYSIS_FETCH_OK', {
          endpoint: currentEndpoint,
          payloadKeys: getPayloadKeys(normalized),
          count: extractedCards.length,
          firstCard: extractedCards[0] || null
        });

        if (!selectedPayload) {
          selectedPayload = normalized;
          selectedEndpoint = currentEndpoint;
        }

        if (extractedCards.length > 0) {
          selectedPayload = normalized;
          selectedCards = extractedCards;
          selectedEndpoint = currentEndpoint;
          break;
        }
      } catch (error) {
        lastError = error;
        console.error('AI_ANALYSIS_FETCH_FAILED', {
          endpoint: currentEndpoint,
          status: error?.status,
          message: error?.message,
          payload: error?.payload
        });
      }
    }

    const cachedCards = readCache();
    const finalCards = selectedCards.length > 0 ? selectedCards : cachedCards;
    const finalPayload = selectedPayload || {};

    setCards(finalCards);
    setStatus(finalCards.length > 0 ? 'ready' : (finalPayload?.status || 'empty'));
    setSummary(buildSummary(finalPayload, finalCards));
    setStats(buildStats(finalPayload, finalCards));
    setTodayKey(finalPayload?.todayKey || finalPayload?.dateKey || finalPayload?.date || requestDateKey || getClientIstanbulDateKey());
    setPublicationDate(
      finalPayload?.publishDate ||
      finalPayload?.publicationDateKey ||
      finalPayload?.publicationDate ||
      finalPayload?.meta?.analysisDate ||
      finalPayload?.date ||
      requestDateKey ||
      null
    );
    setLastUpdated(finalPayload?.lastUpdatedAt || finalPayload?.lastPublishedAt || finalPayload?.updatedAt || null);
    setSource(selectedCards.length > 0 ? (finalPayload?.source || 'api') : (finalCards.length > 0 ? 'cache' : 'empty'));
    setRefreshSlot(finalPayload?.refreshSlot || '');
    setMessage(
      selectedCards.length > 0
        ? (finalPayload?.message || '')
        : (finalCards.length > 0 ? 'Son basarili AI yayin cache uzerinden gosteriliyor.' : '')
    );
    setDebug(finalPayload?.debug || null);
    setHistory(Array.isArray(finalPayload?.history) ? finalPayload.history : []);
    setAdminNotice(finalPayload?.adminNotice || '');
    setEndpoint(selectedEndpoint);
    setFetchDebug({
      endpoint: selectedEndpoint,
      payloadKeys: getPayloadKeys(finalPayload),
      extractedCards: selectedCards.length,
      firstCardSample: selectedCards[0] || finalCards[0] || null
    });
    setError(finalCards.length > 0 ? '' : (lastError ? normalizeErrorMessage(lastError) : ''));

    if (selectedCards.length > 0) {
      writeCache(selectedCards);
      clearPendingPoll();
    } else {
      clearPendingPoll();
      timeoutRef.current = window.setTimeout(fetchCards, 15000);
    }

    setLoading(false);
  }, [selectedDateKey]);

  useEffect(() => {
    fetchCards();

    return () => {
      clearPendingPoll();
    };
  }, [fetchCards]);

  return {
    cards,
    status,
    summary,
    stats,
    todayKey,
    publicationDate,
    lastUpdated,
    source,
    refreshSlot,
    message,
    debug,
    history,
    adminNotice,
    loading,
    error,
    endpoint,
    fetchDebug,
    refetch: () => fetchCards()
  };
};
